import {
	ChannelModel,
	NavigateOptions,
	RouteOptions,
} from '@sosocio/creator-types/app';
import * as DB from '@sosocio/creator-types/database';
import { buildProjectWebshopRedirectPath } from '@sosocio/tools/smartlink';
import {
	AxiosRequestConfig,
	AxiosRequestHeaders,
} from 'axios';
import Product from 'classes/product';
import ProductState from 'classes/productstate';
import User from 'classes/user';
import ButtonComponent from 'components/button';
import ajax from 'controllers/ajax';
import analytics from 'controllers/analytics';
import storage from 'controllers/storage';
import upload from 'controllers/upload';
import openUrl from 'ops/open-url';
import openWindow from 'ops/open-window';
import * as DialogService from 'services/dialog';
import getDelegateUrl from 'services/get-delegate-url';
import {
	consumeSmartLinkAttributes,
	getSmartLinkBackUrl,
	hasInheritedProjectCartUrl,
} from 'services/smartlink';
import { OfferingGroups } from 'settings/offerings';
import {
	AppDataModule,
	AppStateModule,
	CartItemsModule,
	CartModule,
	ConfigModule,
	ProductsModule,
	ProductStateModule,
	ThemeDataModule,
	ThemeStateModule,
	UIModule,
	UserModule,
} from 'store';
import { componentSlot } from 'store/ui';
import parseUrl from 'tools/parse-url';
import { isHalfFoldOffering } from 'tools/project/greeting-cards';
import { hasReplaceableProductContent } from 'tools/replaceable-product-content';
import _ from 'underscore';
import reportError from 'utils/report-error';
import FramePickerView from 'views/frame-picker';
import OfferingOptionsView from 'views/offering-options';
import { openUploadErrorsDialog } from 'views/upload-errors/dialog';

class Navigate {
	history: string[];

	constructor() {
		this.history = [
			window.location.pathname.replace(
				'/app',
				'',
			),
		];
	}

	/**
	 * Navigates within the SPA, deferring pending updates while switching pages in the same editor.
	 * A saved project can reload an update when navigation leaves its current editor.
	 *
	 * @param route Application-relative route without the leading slash.
	 * @param routeOptions Router behavior for the navigation.
	 * @param opts Optional query and hash fragments appended to the route.
	 */
	public go(
		route: string,
		routeOptions: RouteOptions,
		opts?: NavigateOptions,
	): void {
		const options = opts || {};

		if (options.query) {
			route += `?${options.query}`;
		}
		if (options.hash) {
			route += `#${options.hash}`;
		}

		const editorPagePath = /^\/?(\d+)\/edit\/[^/?#]+\/?(?:[?#]|$)/u;
		const currentEditorProductId = window.App.router.$route?.path.match(editorPagePath)?.[1];
		const nextEditorProductId = route.match(editorPagePath)?.[1];
		const isEditorPageNavigation = currentEditorProductId !== undefined
			&& currentEditorProductId === nextEditorProductId;

		if (
			(window.swUpdate || window.dataUpdate)
			&& ProductStateModule.getSaved
			&& !hasInheritedProjectCartUrl()
			&& !isEditorPageNavigation
		) {
			// Service worker and/or the app data is updated, reload for new version of app
			console.log('ServiceWorker/AppCache and/or appData update: reload location href');

			let l = `${window.location.protocol}//${window.location.host}/app`;

			if (route.length) {
				l += `/${route}`;
			}

			window.location.href = l;
		} else {
			// If this route replaces the previous, then remove the previous from the history array
			if (routeOptions.replace) {
				this.history.pop();
			}

			// Push route to history array
			this.history.push(route);
			window.App.router.navigate(
				`/${route}`,
				routeOptions,
			);
		}
	}

	public back(
		options?: NavigateOptions,
		fallback?: (o?: NavigateOptions) => void,
	) {
		options = _.extend(
			{
				slide: true,
				slideDirection: 'right',
			},
			options,
		);

		// remove current route
		this.history.pop();

		if (this.history.length === 0 && fallback) {
			fallback(options);
		} else {
			window.App.router.back();
		}
	}

	/**
	 * Leaves for the saved partner destination or follows the existing referrer history.
	 */
	public backToReferrer(): void {
		const backUrl = getSmartLinkBackUrl();

		if (backUrl) {
			openUrl(
				backUrl,
				false,
			);
			return;
		}

		if (this.history.length) {
			return window.history.go(
				-this.history.length,
			);
		}

		return this.back();
	}

	private addUrlParameters(route: string) {
		// Add all active url parameters to the route
		const parsedUrl = parseUrl(window.location.href);
		const urlVars = parsedUrl.parameters;
		route += '?';
		_.each(
			_.pairs(urlVars),
			([
				prop,
				value,
			]) => {
				route += `${prop}=${value}&`;
			},
		);

		return route.substring(
			0,
			route.length - 1,
		);
	}

	/**
	 * Releases pending project launch values when leaving their creation context.
	 * Session defaults and partner destinations retain their existing lifetime.
	 */
	private discardPendingSmartLinkProjectAttributes(): void {
		consumeSmartLinkAttributes([
			'cartUrl',
			'customAttributes',
			'printColors',
			'printPositionIndexes',
			'productOptions',
			'quantity',
		]);
	}

	public openCart() {
		if (!ConfigModule.hasCartModule) {
			// No shopping cart module, go to external webshop

			// Track cart pageview for analytics conversion measurement
			analytics.trackPageView(
				'cart',
				'Shopping cart',
			);

			// Submit cart
			this.toExternalWebshop();
		} else {
			// Go to shoppingcart overview page
			this.toCart();
		}
	}

	private proceedFromCart() {
		// Update all cartitem data on server
		CartItemsModule
			.putModels({
				data: CartItemsModule.collection,
			})
			.then(() => {
				this.toExternalWebshop();
			})
			.catch(DialogService.closeLoaderDialog);
	}

	public proceedFromCreate(
		productModel: DB.ProductModel,
	) {
		if (ThemeStateModule.themeHasPhotoSelect) {
			if (window.nativeToWeb) {
				window.nativeToWeb.pickerDismissed = () => {
					this.back();
				};
			}

			this.toSelect(
				productModel.id,
				{
					showSelection: false,
					removeUnselected: true,
				},
				{
					trigger: true,
					replace: true,
				},
			);
		} else {
			this.openProduct(
				productModel.id,
				{
					replace: true,
				},
			);
		}
	}

	private enhancementCheck(): Promise<void> {
		if (ProductStateModule.getProductSettings.applyEnhancement === undefined) {
			return ProductState.askEnhancementConfirmation();
		}

		return Promise.resolve();
	}

	public goBack(
		source: 'editProduct' | 'end',
	): void {
		const { productId } = ProductStateModule;

		if (!productId) {
			throw new Error('Could not find required productId');
		}

		if (source === 'editProduct') {
			if (ThemeStateModule.themeHasPhotoSelect) {
				if (window.glPlatform === 'native') {
					if (window.nativeToWeb) {
						window.nativeToWeb.pickerDismissed = () => {
							this.toStart();
						};
					}

					return this.toSelect(
						productId,
						{
							showSelection: false,
							removeUnselected: true,
						},
					);
				}

				return this.toLibrary(productId);
			}

			if (!ConfigModule.hasStoreModule) {
				/*
				 * When the store module is not configured, we need the user to keep the browser history,
				 * so that we can navigate back to the referrer. We therefore need to use the back function here.
				 */
				return this.backToReferrer();
			}

			return this.toStart();
		}

		if (source === 'end') {
			// Reset scroll position (in case we are going back to the pages overview)
			AppStateModule.setScrollPosition(0);

			return window.App.router.openProduct(
				productId,
				false,
			);
		}

		throw new Error('Unknown source to goBack from');
	}

	/**
	 * Advances the active product flow, keeping half-fold offerings in their integrated editor.
	 * @param source Current editing or checkout stage.
	 */
	public goForward(source: 'fill' | 'selection' | 'shoppingcart' | 'preview' | 'overview' | 'end' | 'editor' | 'crop'): void {
		if (source == 'fill') {
			const productModel = ProductStateModule.getProduct;
			const { productId } = ProductStateModule;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}
			if (!productId) {
				throw new Error('Could not find required productId');
			}

			// Switch to next step
			if (isHalfFoldOffering(ProductStateModule.getOffering)) {
				this.toEditor(
					productId,
					'0',
				);
			} else if (OfferingGroups(
				productModel.group,
				[
					'BookTypes',
					'PageSets',
				],
			)) {
				this.toOverview(productId);
			} else if (OfferingGroups(
				productModel.group,
				['BasicProducts'],
			)) {
				this.toEditor(
					productId,
					'0',
				);
			} else {
				// Open front page in editor
				this.toPreview(productId);
			}
		} else if (source == 'selection') {
			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}

			const themeModel = ThemeDataModule.getTheme(productModel.themeid);
			const photoModels = ProductStateModule.getPhotosSelected;
			const photoCount = photoModels.length;

			const minphotos = !themeModel || _.isNull(themeModel.minphotos)
				? 1
				: themeModel.minphotos;

			upload.waitForFileConversion().finally(() => {
				if (ProductStateModule.getPhotosFailed.length > 0) {
					openUploadErrorsDialog({
						/** The selection page can add other photos in place of the failed ones. */
						canReplace: true,
						onContinue: () => {
							this.goForward(source);
						},
					});
				} else if (
					photoCount < minphotos
					&& ThemeStateModule.themeHasPhotoSelect
				) {
					analytics.trackEvent(
						'Invalid photo count',
						{
							category: 'Error',
							label: 'Product photo count',
							value: photoCount,
						},
					);

					UIModule.openDialog({
						header: componentSlot({
							slot: window.App.router.$t('dialogHeaderNotEnoughPhotosSelected'),
						}),
						body: componentSlot({
							slot: window.App.router.$t(
								'dialogTextNotEnoughPhotosSelected',
								{
									count: minphotos - photoCount,
								},
							),
						}),
						footer: componentSlot({
							tag: ButtonComponent,
							props: {
								class: 'grow',
								size: 'large',
								onClick: () => {
									if (window.glPlatform == 'native') {
										const maxPhotos = (
											themeModel && themeModel.maxphotos
												? themeModel.maxphotos - ProductStateModule.getPhotosSelected.length
												: undefined
										);

										upload.filePicker(
											maxPhotos,
											{
												showSelection: false,
												removeUnselected: true,
											},
										);
									}

									UIModule.closeDialog();
								},
							},
							slot: window.App.router.$t('dialogButtonErrorOk'),
						}),
						props: {
							onClose: UIModule.closeDialog,
							'onClick:outside': UIModule.closeDialog,
						},
					});
				} else {
					const productSettings = ProductStateModule.getProductSettings;

					ProductState
						.addProductProperties()
						.finally(() => {
							if (
								OfferingGroups(
									productModel.group,
									[
										'WallDecoration',
										'BookTypes',
										'Cards',
									],
								)
								&& (
									typeof productSettings.autoFill === 'undefined'
									|| productSettings.autoFill
								)
								&& hasReplaceableProductContent({
									objectModels: ProductStateModule.getObjects,
									themePageStateModels: ThemeStateModule.pageStateCollection,
								})
							) {
								UIModule.openDialog({
									header: componentSlot({
										slot: window.App.router.$t('dialogHeaderReplaceContent'),
									}),
									body: componentSlot({
										slot: window.App.router.$t('dialogTextReplaceContent'),
									}),
									footer: [
										componentSlot({
											tag: ButtonComponent,
											props: {
												color: 'secondary',
												class: 'w-full',
												size: 'large',
												onClick: () => {
													const { productId } = ProductStateModule;

													if (!productId) {
														UIModule.closeDialog();
														throw new Error('Missing product id');
													}

													ProductState
														.select(productId)
														.then(() => Product.fill({
															replaceContent: true,
															showBuildOptions: false,
														}))
														.catch(() => {
															if (isHalfFoldOffering(ProductStateModule.getOffering)) {
																this.toEditor(
																	productId,
																	'0',
																);
															} else if (
																OfferingGroups(
																	productModel.group,
																	[
																		'BookTypes',
																		'PageSets',
																	],
																)
															) {
																this.toOverview(productId);
															} else if (
																OfferingGroups(
																	productModel.group,
																	['BasicProducts'],
																)
															) {
																this.toEditor(
																	productId,
																	'0',
																);
															} else {
																// Open front page in editor
																this.toPreview(productId);
															}
														});

													UIModule.closeDialog();
												},
											},
											slot: window.App.router.$t('dialogButtonReplaceContentOk'),
										}),
										componentSlot({
											tag: ButtonComponent,
											props: {
												color: 'primary',
												class: 'w-full',
												size: 'large',
												onClick: () => {
													if (ProductStateModule.productId) {
														if (isHalfFoldOffering(ProductStateModule.getOffering)) {
															this.toEditor(
																ProductStateModule.productId,
																'0',
															);
														} else if (OfferingGroups(
															productModel.group,
															[
																'BookTypes',
																'PageSets',
															],
														)) {
															this.toOverview(ProductStateModule.productId);
														} else if (OfferingGroups(
															productModel.group,
															['BasicProducts'],
														)) {
															this.toEditor(
																ProductStateModule.productId,
																'0',
															);
														} else {
															// Open front page in editor
															this.toPreview(ProductStateModule.productId);
														}
													}

													UIModule.closeDialog();
												},
											},
											slot: window.App.router.$t('dialogButtonReplaceContentCancel'),
										}),
									],
									props: {
										class: '[&>footer]:flex-col',
										onClose: UIModule.closeDialog,
										'onClick:outside': UIModule.closeDialog,
									},
								});
							} else {
								Product.fill();
							}
						});
				}
			});
		} else if (source == 'shoppingcart') {
			if (AppStateModule.online) {
				const closeLoader = DialogService.openLoaderDialog();

				const removeData = _.filter(
					CartItemsModule.collection,
					(cartItemModel) => cartItemModel.quantity === 0 && !cartItemModel.shoppingcartitemid,
				);

				if (removeData.length) {
					const putModels = _.after(
						removeData.length,
						() => {
							this.proceedFromCart();
						},
					);
					_.each(
						removeData,
						(modelData) => {
							CartItemsModule
								.destroyModel({
									id: modelData.id,
									methodOptions: {
										auth: true,
										debug: {
											abort: true,
										},
									},
								})
								.then(
									() => {
										putModels();
									},
									() => {
										closeLoader();
									},
								);
						},
					);
				} else {
					this.proceedFromCart();
				}
			} else {
				// Notify user that (s)he needs to be online to go to the webshop
				const closeError = DialogService.openErrorDialog({
					header: {
						title: window.App.router.$t('dialogHeaderUnavailable'),
					},
					body: {
						content: window.App.router.$t('dialogTextUnavailableWebshop'),
					},
					footer: {
						buttons: [
							{
								id: 'accept',
								text: window.App.router.$t('dialogButtonUnavailableOk'),
								click: () => {
									closeError();
								},
							},
						],
					},
				});
			}
		} else if (source == 'editor') {
			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}

			if (isHalfFoldOffering(ProductStateModule.getOffering) || OfferingGroups(
				productModel.group,
				['BasicProducts'],
			)) {
				this.toEnd(productModel.id);
			} else {
				this.back(
					{},
					() => {
						this.openProduct(
							productModel.id,
							{
								trigger: true,
								replace: true,
							},
						);
					},
				);
			}
		} else if (source == 'preview' || source == 'overview') {
			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}

			if (OfferingGroups(
				productModel.group,
				['PageSets'],
			)
				&& !OfferingGroups(
					productModel.group,
					['PhotoSheets'],
				)
				&& ProductStateModule.getCroppedPages.length
			) {
				this.toCrop(productModel.id);
			} else {
				ProductState.finalize(
					true,
					false,
				).then((saved) => {
					if (saved) {
						this.toEnd(productModel.id);
					}
				}).catch((err) => {
					if (err?.message) {
						DialogService.openErrorDialog({
							body: {
								content: err.message,
							},
						});
					}
				});
			}
		} else if (source == 'crop') {
			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Could not find required product model');
			}

			ProductState.finalize(
				true,
				false,
			).then((saved) => {
				if (saved) {
					this.toEnd(productModel.id);
				}
			}).catch((err) => {
				if (err?.message) {
					DialogService.openErrorDialog({
						body: {
							content: err.message,
						},
					});
				}
			});
		} else if (source == 'end') {
			this.toExternalWebshop();
		}
	}

	/**
	 * Opens the product's editing entry point; schema-defined half-fold products start on their front page.
	 *
	 * @param productid Product to open.
	 * @param routeOpts Router behavior for this navigation.
	 * @param navigateOpts Transition, query and hash options.
	 */
	public openProduct(
		productid: number,
		routeOpts?: RouteOptions,
		navigateOpts?: NavigateOptions,
	): void {
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			throw new Error('Could not find required product model');
		}

		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		if (isHalfFoldOffering(ProductStateModule.getOffering)) {
			this.toEditor(
				productid,
				'0',
				routeOptions,
				navigateOpts,
			);
		} else if (
			OfferingGroups(
				productModel.group,
				[
					'BookTypes',
					'PageSets',
				],
			)
		) {
			this.toOverview(
				productid,
				undefined,
				routeOptions,
				navigateOpts,
			);
		} else if (
			OfferingGroups(
				productModel.group,
				['PostCards'],
			)
		) {
			this.toPreview(
				productid,
				undefined,
				routeOptions,
				navigateOpts,
			);
		} else {
			this.toEditor(
				productid,
				'0',
				routeOptions,
				navigateOpts,
			);
		}
	}

	/**
	 * Returns to the start page and discards project attributes from an abandoned launch.
	 *
	 * @param routeOpts Router behavior for the start-page navigation.
	 * @param opts Transition and URL options passed to the navigation controller.
	 */
	public toStart(
		routeOpts?: RouteOptions,
		opts?: NavigateOptions,
	): void {
		this.discardPendingSmartLinkProjectAttributes();

		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const options = _.extend(
			{
				slideDirection: 'right',
			},
			opts,
		);

		this.go(
			'',
			routeOptions,
			options,
		);
	}

	public toCreate(
		objParameters: {
			themeid?: number;
			offeringid?: number;
			themecatid?: number;
			listeritemid?: number;
		},
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		objParameters = _.extend(
			{},
			objParameters,
		);
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		let route = '';

		if (
			'themeid' in objParameters
			&& objParameters.themeid !== null
		) {
			route += `create/${objParameters.listeritemid}/${objParameters.offeringid}/${objParameters.themecatid || 0}/${objParameters.themeid}`;
		} else if (
			'themecatid' in objParameters
			&& objParameters.themecatid !== null
		) {
			route += `create/${objParameters.listeritemid}/${objParameters.offeringid}/${objParameters.themecatid}`;
		} else if (
			'offeringid' in objParameters
			&& objParameters.offeringid !== null
		) {
			route += `create/${objParameters.listeritemid}/${objParameters.offeringid}`;
		} else if (
			'listeritemid' in objParameters
			&& objParameters.listeritemid !== null
		) {
			route += `create/${objParameters.listeritemid}`;
		} else {
			route += 'create';
		}

		route = this.addUrlParameters(route);

		this.go(
			route,
			routeOptions,
			options,
		);
	}

	public toPDP(
		pdpid: DB.PDPModel['id'],
		_options?: Record<string, any>,
		routeOpts?: RouteOptions,
		navigateOpts?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const route = this.addUrlParameters(`pdp/${pdpid}`);
		this.go(
			route,
			routeOptions,
			navigateOpts,
		);
	}

	public toLibrary(
		productid: number,
		photoid?: number,
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		const route = typeof photoid !== 'undefined'
			? `${productid}/library/${photoid}`
			: `${productid}/library`;
		this.go(
			route,
			routeOptions,
			options,
		);
	}

	public toSelect(
		productid: number,
		options: {
			channel?: ChannelModel['id'];
			// Do we open up the native screen with displaying the current selection?
			showSelection: boolean;
			// Should we remove unselected photos in the native app from the product?
			removeUnselected: boolean;
		},
		routeOpts?: RouteOptions,
		navigateOpts?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		const route = options && options.channel
			? `${productid}/select/${options.channel}`
			: `${productid}/select`;
		this.go(
			route,
			routeOptions,
			navigateOpts,
		);

		if (window.glPlatform == 'native') {
			const { themeModel } = ThemeStateModule;
			const maxPhotos = themeModel && themeModel.maxphotos
				? themeModel.maxphotos - ProductStateModule.getPhotosSelected.length
				: undefined;

			upload.filePicker(
				maxPhotos,
				{
					showSelection: !!(options && options.showSelection),
					removeUnselected: !!(options && options.removeUnselected),
				},
			);
		}
	}

	public toOverview(
		productid: number,
		_pagenr?: string,
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		this.go(
			`${productid}/overview`,
			routeOptions,
			options,
		);
	}

	public toPreview(
		productid: number,
		pagenr?: string,
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const productModel = ProductsModule.getById(productid);

		if (typeof pagenr === 'undefined' || pagenr === null) {
			if (productModel && OfferingGroups(
				productModel.group,
				['BookTypes'],
			)) {
				pagenr = '0-1';
			} else if (productModel && OfferingGroups(
				productModel.group,
				['Cards'],
			)) {
				pagenr = '1';
			} else {
				pagenr = '0';
			}
		}

		this.go(
			`${productid}/preview/${pagenr}`,
			routeOptions,
			options,
		);
	}

	public toEditor(
		productid: number,
		pagenr: number | string,
		routeOpts?: RouteOptions,
		opts?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const options = _.extend(
			{
				slideDirection: 'up',
			},
			opts,
		);

		this.go(
			`${productid}/edit/${pagenr}`,
			routeOptions,
			options,
		);
	}

	public toSort(
		productid: number,
		routeOpts?: RouteOptions,
		opts?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const options = _.extend(
			{
				slideDirection: 'up',
			},
			opts,
		);

		this.go(
			`${productid}/sort`,
			routeOptions,
			options,
		);
	}

	public toCrop(
		productid: number,
		objectid?: string,
		routeOpts?: RouteOptions,
		opts?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		const options = _.extend(
			{
				slideDirection: 'up',
			},
			opts,
		);

		let route = `${productid}/crop`;

		if (objectid) {
			route += `/${objectid}`;
		}

		this.go(
			route,
			routeOptions,
			options,
		);
	}

	public toEnd(
		productid: number,
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		const productModel = ProductStateModule.getProduct;
		const countryModel = UserModule.countryid
			? AppDataModule.getCountry(UserModule.countryid)
			: undefined;

		if (productModel && countryModel) {
			this.enhancementCheck()
				.finally(
					() => this.offeringOptionsCheck(
						false,
					),
				)
				.finally(() => {
					const routeOptions = _.extend(
						{
							trigger: true,
						},
						routeOpts,
					);
					this.go(
						`${productid}/end`,
						routeOptions,
						options,
					);
				});
		} else {
			const routeOptions = _.extend(
				{
					trigger: true,
				},
				routeOpts,
			);
			this.go(
				`${productid}/end`,
				routeOptions,
				options,
			);
		}
	}

	public toConfirm(
		productid: number,
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		this.go(
			`${productid}/confirm`,
			routeOptions,
			options,
		);
	}

	/**
	 * Opens the internal cart and releases attributes from any abandoned creation.
	 *
	 * @param routeOpts Router behavior for the cart navigation.
	 */
	public toCart(routeOpts?: RouteOptions): void {
		this.discardPendingSmartLinkProjectAttributes();

		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);
		this.go(
			'cart',
			routeOptions,
		);
	}

	/**
	 * Finalizes the active project and hands its cart to the configured external webshop.
	 * Keeps web checkout blocked until departure and clears its overlay when browser Back restores the page.
	 */
	private toExternalWebshop(): void {
		ProductState
			.finalize(
				true,
				false,
			)
			.then((saved) => {
				if (saved) {
					const closeLoader = DialogService.openLoaderDialog();

					/**
					 * Releases only this checkout's loader when the browser restores its cached page.
					 * @param event Browser page transition, distinguishing restoration from initial loading.
					 */
					function onCheckoutPageShow(event: PageTransitionEvent): void {
						if (event.persisted) {
							window.removeEventListener(
								'pageshow',
								onCheckoutPageShow,
							);
							closeLoader();
						}
					}

					User
						.save()
						.then(() => CartModule.snapshot())
						.then((cartItemModels) => (
							CartItemsModule.reset({
								data: cartItemModels,
							})
						))
						.then(User.syncExternalCart)
						.then(() => {
							if (CartItemsModule.collection.length === 0) {
								const closeError = DialogService.openErrorDialog({
									body: {
										content: window.App.router.$t('dialogTextCartContentChanged'),
									},
									footer: {
										buttons: [
											{
												id: 'accept',
												text: window.App.router.$t('dialogButtonErrorOk'),
												click: () => {
													window.location.reload();
													closeError();
												},
											},
										],
									},
								});

								throw new Error('No items in cart');
							}
						})
						.then(() => {
							const hyperlinkModel = AppDataModule.findHyperlink({
								tag: 'cartApi',
							});

							if (!hyperlinkModel?.url) {
								return undefined;
							}

							return ajax
								.request(
									{
										method: 'delete',
										withCredentials: true,
										url: hyperlinkModel.url,
									},
									{
										auth: false,
									},
								)
								.catch(() => {
									// Ignore
								});
						})
						.then(() => UserModule.getShopId())
						.then((shopId) => {
							/*
							 * Note: there's a "hack" to make the shop integration working for Hema (2), HelloPrint (19), and Brandon (22)
							 * this could be removed once they are moved to the cart integration "pull" method
							 */
							const externalWebhopEndpoint = [
								2,
								19,
								22,
							].indexOf(ConfigModule.partnerID) !== -1 && shopId
								? '/api/webshop/redirect'
								: ConfigModule['webshop.external.endpoint'];

							if (!externalWebhopEndpoint) {
								throw new Error('Missing external webshop endpoint setting');
							}

							/*
							 * Note: there's a "hack" to make the shop integration working for Hema (2), HelloPrint (19) and Brandon (22)
							 * this could be removed once they are moved to the cart integration "pull" method
							 */
							const externalWebshopMethod = [
								2,
								19,
								22,
							].indexOf(ConfigModule.partnerID) !== -1 && shopId
								? 'GET'
								: ConfigModule['webshop.external.method'].toLocaleLowerCase();

							const headers: AxiosRequestHeaders = {} as AxiosRequestHeaders;

							if (window.glPlatform === 'native') {
								if (
									window.nativeDeviceDetails
									&& window.nativeDeviceDetails.appVersion
								) {
									headers['X-Native-App-Version'] = window.nativeDeviceDetails.appVersion;
								} else {
									const err = (
										window.nativeDeviceDetails
											? new Error('Could not find nativeDeviceDetails')
											: new Error('Could not find appVersion in nativeDeviceDetails')
									);
									reportError(
										err,
										'warning',
									);
								}
							}

							const requestOptions: AxiosRequestConfig = {
								url: externalWebhopEndpoint,
								method: externalWebshopMethod,
								headers,
							};
							const rootCartProjectIds = Array.from(new Set(
								CartItemsModule.collection
									.filter((cartItemModel) => !cartItemModel.shoppingcartitemid)
									.map((cartItemModel) => cartItemModel.productid),
							));
							const activeProjectId = ProductStateModule.productId;
							const redirectProjectId = (
								activeProjectId && rootCartProjectIds.includes(activeProjectId)
									? activeProjectId
									: (
										rootCartProjectIds.length === 1
											? rootCartProjectIds[0]
											: undefined
									)
							);

							if (
								externalWebhopEndpoint === '/api/webshop/redirect'
								&& externalWebshopMethod.toUpperCase() === 'GET'
							) {
								requestOptions.url = buildProjectWebshopRedirectPath(redirectProjectId);
							}

							if (ConfigModule['webshop.external.method'] === 'POST') {
								requestOptions.data = {
									cart: CartModule.getState,
									locale: window.locale,
								};
							}

							return ajax
								.request(
									requestOptions,
									{
										auth: true,
										debug: {
											dialog: true,
											abort: true,
										},
									},
								)
								.catch((error) => {
									if (error.response?.status === 400) {
										CartItemsModule.reset();
									}
								});
						})
						.then((response) => {
							if (!response?.data) {
								throw new Error('Missing response data');
							}

							if (!('url' in response.data)) {
								throw new Error('Missing url property in response');
							} else {
								if (window.glPlatform !== 'native') {
									window.addEventListener(
										'pageshow',
										onCheckoutPageShow,
									);
								}

								openUrl(
									response.data.url,
									false,
								);

								if (window.glPlatform === 'native') {
									closeLoader();
									this.toStart();
								}
							}
						})
						.catch(() => {
							window.removeEventListener(
								'pageshow',
								onCheckoutPageShow,
							);
							closeLoader();

							if (!AppStateModule.online) {
								// Show offline message to user
								DialogService.openErrorDialog({
									header: {
										title: window.App.router.$t('dialogHeaderOffline'),
									},
									body: {
										content: window.App.router.$t('dialogTextOffline'),
									},
								});
							}
						});
				} else {
					/*
					 * finalize was cancelled or its uploads failed; release the
					 * loader the caller opened so the app stays interactive
					 */
					DialogService.closeLoaderDialog();
				}
			})
			.catch((err) => {
				if (err?.message) {
					DialogService.openErrorDialog({
						body: {
							content: err.message,
						},
					});
				}
			});
	}

	/**
	 * Opens the configured account destination, abandoning launch fields on internal navigation.
	 *
	 * @param routeOpts Router behavior for an internal account navigation.
	 * @param options Transition and URL options for an internal account navigation.
	 */
	public toUserAccount(
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	): void {
		const hyperlinkModel = AppDataModule.findHyperlink({
			tag: 'account',
		});

		if (hyperlinkModel) {
			if (hyperlinkModel.delegate) {
				const closeLoader = DialogService.openLoaderDialog();

				getDelegateUrl(hyperlinkModel.url)
					.then((delegateUrl) => {
						openWindow(
							delegateUrl,
							{
								width: undefined,
								height: undefined,
								inApp: true,
							},
						);
					})
					.finally(closeLoader);
			} else {
				openWindow(
					hyperlinkModel.url,
					{
						width: undefined,
						height: undefined,
						inApp: true,
					},
				);
			}
		} else {
			this.discardPendingSmartLinkProjectAttributes();

			const routeOptions = _.extend(
				{
					trigger: true,
				},
				routeOpts,
			);
			this.go(
				'account',
				routeOptions,
				options,
			);
		}
	}

	/**
	 * Opens the account photo library and releases attributes from any abandoned creation.
	 *
	 * @param routeOpts Router behavior for the account photo navigation.
	 * @param options Transition and URL options for the account photo navigation.
	 */
	public toUserAccountPhotos(
		routeOpts?: RouteOptions,
		options?: NavigateOptions,
	): void {
		this.discardPendingSmartLinkProjectAttributes();

		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		this.go(
			'account/photos',
			routeOptions,
			options,
		);
	}

	public toOrderDetails(
		orderid: number,
		routeOpts?: RouteOptions,
	) {
		const routeOptions = _.extend(
			{
				trigger: true,
			},
			routeOpts,
		);

		this.go(
			`account/order/${orderid}`,
			routeOptions,
		);
	}

	private offeringOptionsCheck(
		skipFrame: boolean,
	): Promise<void> {
		// Get the data for the active project
		const productModel = ProductStateModule.getProduct;

		if (!productModel) {
			throw new Error('Missing required product model');
		}

		// Get the country model for the active user
		const countryModel = UserModule.countryid
			? AppDataModule.getCountry(UserModule.countryid)
			: undefined;

		if (!countryModel) {
			throw new Error('Missing required country model');
		}

		// Find the offering details related to the active project
		const searchProps: Partial<DB.OfferingModel> = {
			groupid: productModel.group,
			typeid: productModel.typeid,
		};

		if (productModel.variantid) {
			searchProps.variantid = productModel.variantid;
		}

		const offeringTypes = AppDataModule.findOffering(searchProps);
		const offeringModel = offeringTypes[0];
		const activeOptionValueModels = AppDataModule.getOfferingOptionValueModels(
			offeringModel.id,
			countryModel.regionid,
		);

		// Get all ids of the relevant offering options
		const offeringOptions = activeOptionValueModels.map(
			(model) => model.offeringoptionid,
		);

		// Get all offering options with multiple values, so user can pick
		const optionIds = _.uniq(offeringOptions).filter(
			(optionId) => offeringOptions.filter((x) => x == optionId).length > 1,
		);

		// Filter option models by the ones that should be shown after edit
		const filteredOptionIds = optionIds.filter((id) => {
			const optionModel = AppDataModule.offeringoptions.find(
				(model) => model.id === id,
			);
			return optionModel
				&& optionModel.showafteredit
				&& (!skipFrame || optionModel.tag !== 'frame');
		});

		// If there are not choices to make, we end this function execution
		if (filteredOptionIds.length === 0) {
			return Promise.resolve();
		}

		if (
			OfferingGroups(
				productModel.group,
				['BasicProducts'],
			)
		) {
			const pageModel = ProductStateModule.getPageByNumber(0);
			const frameOptionId = filteredOptionIds.find((optionId) => {
				const optionModel = AppDataModule.offeringoptions.find(
					(model) => model.id === optionId,
				);
				return optionModel?.tag === 'frame';
			});

			if (frameOptionId) {
				const frameOptionValues = AppDataModule.offeringoptionvalues.filter(
					(model) => model.offeringoptionid === frameOptionId,
				);
				const noneFrameOptionValue = frameOptionValues.find(
					(model) => model.tag === 'none',
				);

				if (noneFrameOptionValue && frameOptionValues.length > 1) {
					const isCurrentlyNone = AppDataModule.offeringoptionvalueofferinglinks.find(
						(model) => model.offeringid === offeringModel.id
							&& model.offeringoptionvalueid === noneFrameOptionValue.id,
					);

					if (isCurrentlyNone) {
						return new Promise((resolve) => {
							const { close: closeDialog } = DialogService.openDialog({
								header: {
									hasCloseButton: false,
								},
								body: {
									component: FramePickerView,
									props: {
										pageModel,
									},
									listeners: {
										closeDialog: () => {
											closeDialog();
										},
										select: (event: ServiceEvent<DB.OfferingOptionValueModel>) => {
											analytics.trackEvent(
												'Select upgrade frame',
												{
													groupid: productModel.group,
													typeid: productModel.typeid,
													value: event.payload.value,
												},
											);
										},
									},
									styles: {
										padding: '0',
									},
								},
								listeners: {
									close: () => {
										this
											.offeringOptionsCheck(true)
											.then((resolve));
									},
								},
								maxScreenSize: true,
								styles: {
									padding: '0',
								},
								width: 750,
							});
						});
					}
				}

				return this.offeringOptionsCheck(true);
			}
		}

		return new Promise((resolve) => {
			const { close: closeDialog } = DialogService.openDialog({
				header: {
					hasCloseButton: false,
					title: window.App.router.$t('dialogHeaderProductOptions'),
				},
				body: {
					component: OfferingOptionsView,
					props: {
						filterTags: ['frame'],
						showAfterEdit: true,
						showDuringEdit: false,
					},
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('buttonSave'),
							click: () => {
								closeDialog();
							},
						},
					],
				},
				listeners: {
					close: () => {
						resolve();
					},
				},
				width: 350,
			});
		});
	}

	restore() {
		// Get app state from local storage
		const storageObject = storage.get('appState');
		// Remove local storage object
		storage.clear(['appState']).finally(() => {
			// Proceed to previous route
			if (storageObject) {
				this.go(
					storageObject.route,
					{
						trigger: true,
						replace: true,
					},
				);
			}
		});
	}
}

export default new Navigate();
